import { FC, ReactNode, useMemo, useState } from 'react';
import { Dropdown, Menu } from 'antd';
import { useQueryState } from 'nuqs';
import { TbPlus } from 'react-icons/tb';

import { useWorkspaceResourceCreateAccess } from '@/hooks/useWorkspaceResourceCreateAccess';
import { dashboardQuery, reportQuery } from '@/queries';
import {
    DropdownMenuLabel,
    DropdownOptionContent,
    DropdownPanel,
    DropdownPanelMenu,
    EmptySection,
    LearnMoreFooter,
} from '@/shared';
import CreateDashboardModal from '@/shared/components/modals/ResourceCreationModals/CreateDashboardModal';
import { useDashboardStore } from '@/stores';
import { useUserPreferences } from '@/stores/database/entities/00-RBAC/99-UserPreferences';
import { Dashboard } from '@/types';

import { HOMEPAGE_VIEW_REGISTRY, HomepageViewKey, resolveHomepageView } from './homepageViewRegistry';

interface HomepageViewSelectDropdownProps {
    children: ReactNode;
}

const HomepageViewSelectDropdown: FC<HomepageViewSelectDropdownProps> = ({ children }): JSX.Element => {
    const [query, setQuery] = useQueryState('dashboard', dashboardQuery);
    const [, setReportQuery] = useQueryState('report', reportQuery);
    const [search, setSearch] = useState('');

    const { preferences, setPreference } = useUserPreferences();

    const homepage = resolveHomepageView({ dashboardIdQuery: query, preference: preferences?.home });

    const { dashboards } = useDashboardStore();
    const createDashboardAccess = useWorkspaceResourceCreateAccess('dashboards', dashboards?.length ?? 0);

    const [createDashboardModalOpen, setCreateDashboardModalOpen] = useState<boolean>(false);

    const handleSetHomepageView = (view: HomepageViewKey, dashboardId: string | null = null) => {
        void setQuery(null);
        void setReportQuery(null);
        setPreference('home', { ...preferences?.home, dashboardId, view });
    };
    const normalizedSearch = search.trim().toLocaleLowerCase();
    const showDefaultHomepage =
        !normalizedSearch || HOMEPAGE_VIEW_REGISTRY.default.searchTerms.includes(normalizedSearch);
    const showTasks = !normalizedSearch || HOMEPAGE_VIEW_REGISTRY.tasks.searchTerms.includes(normalizedSearch);
    const filteredDashboards = useMemo(
        () =>
            normalizedSearch
                ? dashboards.filter((dashboard) => dashboard.name?.toLocaleLowerCase().includes(normalizedSearch))
                : dashboards,
        [dashboards, normalizedSearch],
    );
    const hasOptions = showDefaultHomepage || showTasks || filteredDashboards.length > 0;
    const DefaultHomepageIcon = HOMEPAGE_VIEW_REGISTRY.default.icon;
    const DashboardIcon = HOMEPAGE_VIEW_REGISTRY.dashboard.icon;
    const TasksIcon = HOMEPAGE_VIEW_REGISTRY.tasks.icon;

    return (
        <>
            <Dropdown
                trigger={['click']}
                placement='bottomRight'
                popupRender={() => (
                    <DropdownPanel
                        actions={
                            <DropdownPanelMenu selectable={false}>
                                <Menu.Item
                                    disabled={createDashboardAccess.disabled}
                                    key='create'
                                    title={createDashboardAccess.disabledReason}
                                    onClick={() => {
                                        if (!createDashboardAccess.disabled) {
                                            setCreateDashboardModalOpen(true);
                                        }
                                    }}
                                >
                                    <TbPlus size='1rem' style={{ marginRight: '0.25rem', verticalAlign: '-0.2rem' }} />
                                    Create Dashboard
                                </Menu.Item>
                            </DropdownPanelMenu>
                        }
                        footer={<LearnMoreFooter topic='Homepage views & dashboard settings' variant='dropdown' />}
                        search={{
                            placeholder: 'Search Homepage Views...',
                            setValue: setSearch,
                            value: search,
                        }}
                        style={{ marginTop: '0.75rem' }}
                        width={16 * 20}
                    >
                        {showDefaultHomepage || showTasks ? (
                            <>
                                <DropdownMenuLabel>Platform Views</DropdownMenuLabel>
                                <DropdownPanelMenu selectable={false}>
                                    {showDefaultHomepage ? (
                                        <Menu.Item key='default' onClick={() => handleSetHomepageView('default')}>
                                            <DropdownOptionContent
                                                control={<DefaultHomepageIcon size='1rem' />}
                                                selected={homepage.view === 'default'}
                                                title={HOMEPAGE_VIEW_REGISTRY.default.label}
                                            />
                                        </Menu.Item>
                                    ) : null}
                                    {showTasks ? (
                                        <Menu.Item key='tasks' onClick={() => handleSetHomepageView('tasks')}>
                                            <DropdownOptionContent
                                                control={<TasksIcon size='1rem' />}
                                                selected={homepage.view === 'tasks'}
                                                title={HOMEPAGE_VIEW_REGISTRY.tasks.label}
                                            />
                                        </Menu.Item>
                                    ) : null}
                                </DropdownPanelMenu>
                            </>
                        ) : null}
                        {filteredDashboards.length ? (
                            <>
                                <DropdownMenuLabel>Custom Dashboards</DropdownMenuLabel>
                                <DropdownPanelMenu selectable={false}>
                                    {filteredDashboards.map((dashboard: Dashboard) => (
                                        <Menu.Item
                                            key={dashboard.id}
                                            onClick={() => handleSetHomepageView('dashboard', dashboard.id)}
                                        >
                                            <DropdownOptionContent
                                                icon={<DashboardIcon size='0.875rem' />}
                                                selected={
                                                    homepage.view === 'dashboard' &&
                                                    homepage.dashboardId === dashboard.id
                                                }
                                                title={dashboard.name}
                                            />
                                        </Menu.Item>
                                    ))}
                                </DropdownPanelMenu>
                            </>
                        ) : null}
                        {!hasOptions ? (
                            <EmptySection
                                height='12.5rem'
                                icon={<DashboardIcon size='1.25rem' />}
                                label='No Homepage Views Found'
                                description='Try another view or dashboard name.'
                            />
                        ) : null}
                    </DropdownPanel>
                )}
            >
                <span>{children}</span>
            </Dropdown>
            <CreateDashboardModal open={createDashboardModalOpen} setOpen={setCreateDashboardModalOpen} />
        </>
    );
};

export default HomepageViewSelectDropdown;
